import React, { useEffect, useRef, useState } from 'react';
import { Provider, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { BrowserRouter as Router, Route, Routes, useNavigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { ToastContainer } from 'react-toastify';
import store from './redux/store';
import Admin from './routes/admin';
import Auth from './routes/auth';
import './static/css/style.css';
import config from './config/config';
import { fontFamily, fontSizeBase } from './config/theme/themeVariables';
import ProtectedRoute from './components/utilities/protectedRoute';
import PostResetPasswordNavigate from './components/utilities/PostResetPasswordNavigate';
import RootRedirect from './components/utilities/RootRedirect';
import 'antd/dist/antd.less';
import 'react-toastify/dist/ReactToastify.css';
import Pay from './container/pages/Pay';
import PaymentVerifyPage from './container/pages/PaymentVerifyPage';
import InvalidSlugPage from './container/pages/InvalidSlugPage';
import VerifyAlmaOnboardingStatus from './container/pages/VerifyAlmaOnboardingStatusPage';
import MerchantRegistrationRoutes from './routes/merchant-resistration';
import MerchantPaymentReturnRoute from './components/utilities/MerchantPaymentReturnRoute';

// const NotFound = lazy(() => import('./container/pages/404'));

const { themeColor } = config;

/** After logout API success, redirect to Auth (login) page only — avoids blank flash on "/" */
function LogoutRedirect() {
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => state?.auth?.login);
  const prevIsLoggedIn = useRef(isLoggedIn);
  useEffect(() => {
    if (prevIsLoggedIn.current && !isLoggedIn) {
      navigate('/', { replace: true });
    }
    prevIsLoggedIn.current = isLoggedIn;
  }, [isLoggedIn, navigate]);
  return null;
}

function ProviderConfig() {
  const { rtl, isLoggedIn, topMenu, mainContent } = useSelector((state) => {
    return {
      rtl: state.ChangeLayoutMode.rtlData,
      topMenu: state.ChangeLayoutMode.topMenu,
      mainContent: state.ChangeLayoutMode.mode,
      isLoggedIn: state?.auth?.login,
    };
  });

  const [path, setPath] = useState(window.location.pathname);
  const UsersProfile = useSelector((state) => state?.auth.user);
  useEffect(() => {
    let unmounted = false;
    if (!unmounted) {
      setPath(window.location.pathname);
    }
    // eslint-disable-next-line no-return-assign
    return () => (unmounted = true);
  }, [setPath]);

  // Sync global font variables from theme (single source of truth)
  useEffect(() => {
    document.documentElement.style.setProperty('--font-family', fontFamily);
    document.documentElement.style.setProperty('--font-size-base', fontSizeBase);
  }, []);

  return (
    <ConfigProvider direction={rtl ? 'rtl' : 'ltr'}>
      <>
        <ThemeProvider theme={{ ...themeColor, rtl, topMenu, mainContent }}>
          <>
            <Router basename={process.env.PUBLIC_URL}>
              <LogoutRedirect />
              {!isLoggedIn ? (
                <Routes>
                  <Route path="/merchant/payment/success" element={<MerchantPaymentReturnRoute />} />
                  <Route path="/pay/:id" element={<Pay />} />
                  <Route path="/onboarding/*" element={<MerchantRegistrationRoutes />} />

                  <Route path="/verify-payment-status" element={<VerifyAlmaOnboardingStatus />} />

                  <Route path="pay/verify-page" element={<PaymentVerifyPage />} />
                  <Route path="invalid-slug" element={<InvalidSlugPage />} />
                  <Route path="/*" element={<Auth />} />
                </Routes>
              ) : (
                <>
                  <PostResetPasswordNavigate />
                  <Routes>
                    <Route path="/onboarding/*" element={<MerchantRegistrationRoutes />} />

                    <Route path="/pay/:id" element={<Pay />} />
                    <Route path="/merchant/payment/success" element={<MerchantPaymentReturnRoute />} />
                    <Route path="/verify-payment-status" element={<VerifyAlmaOnboardingStatus />} />
                    <Route path="/pay/verify-page" element={<PaymentVerifyPage />} />
                    <Route path="invalid-slug" element={<InvalidSlugPage />} />
                    <Route path="/" element={<RootRedirect />} />
                    <Route path="/*" element={<ProtectedRoute Component={Admin} />} />
                  </Routes>
                </>
              )}

              {/* {isLoggedIn && UsersProfile?.role === 'merchant' && UsersProfile?.current_step === 6 && (
                <Routes>
                  <Route path="/" element={<Navigate to="/profile" />} />
                </Routes>
              )}
              {isLoggedIn && UsersProfile?.role === 'super_admin' && (
                <Routes>
                  <Route path="/" element={<Navigate to="/merchants" />} />
                </Routes>
              )} */}
            </Router>
          </>
        </ThemeProvider>
      </>
    </ConfigProvider>
  );
}

function App() {
  return (
    <Provider store={store.reduxStore}>
      <ProviderConfig />
      <ToastContainer />
    </Provider>
  );
}

export default App;
